import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { socialApi, type YouTubeVideo } from "@/lib/socialApi";
import { trackVideoPlay } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";

const HOME_VIDEO_LIMIT = 4;

const STEPS = [
    { num: "01", labelEn: "DISCOVER", key: "discover" as const },
    { num: "02", labelEn: "DESIGN", key: "design" as const },
    { num: "03", labelEn: "BUILD", key: "build" as const },
    { num: "04", labelEn: "LAUNCH", key: "launch" as const },
];

const useFormatDate = () => {
    const { lang } = useLanguage();
    return (iso?: string) => {
        if (!iso) return "";
        try {
            return new Intl.DateTimeFormat(lang, { year: "numeric", month: "long" }).format(
                new Date(iso),
            );
        } catch {
            return "";
        }
    };
};

const HeroVideoBlock = ({ video }: { video: YouTubeVideo }) => {
    const { t } = useTranslation();
    const formatDate = useFormatDate();
    const [playing, setPlaying] = useState(false);
    const embedSrc = `${video.embedUrl}?autoplay=1&rel=0`;

    return (
        <div className="flex flex-col">
            {/* Large 9:16 thumbnail, centered within the card */}
            <div className="relative mx-auto w-full max-w-[220px] aspect-[9/16] rounded-[20px] overflow-hidden border border-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]">
                {playing ? (
                    <iframe
                        src={embedSrc}
                        title={video.title}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full border-0"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => {
                            trackVideoPlay(video.title, video.id);
                            setPlaying(true);
                        }}
                        aria-label={t("videos.play_aria", { title: video.title })}
                        className="absolute inset-0 w-full h-full group/thumb"
                        data-cursor="hover"
                    >
                        <img
                            src={video.thumbnail}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(.23,1,.32,1)] group-hover/thumb:scale-[1.06]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent" />
                        <span className="absolute top-3 right-3 font-v2-label text-[9px] px-2 py-0.5 rounded-full bg-background/70 backdrop-blur-sm border border-white/10 text-white/80">
                            SHORT
                        </span>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div
                                className="w-14 h-14 rounded-full flex items-center justify-center text-white transition-transform duration-300 group-hover/thumb:scale-110"
                                style={{
                                    background: "var(--gradient-accent-v2)",
                                    boxShadow:
                                        "0 12px 28px -4px hsl(260 100% 55% / 0.6)",
                                }}
                            >
                                <Play size={20} className="ml-0.5" fill="currentColor" />
                            </div>
                        </div>
                    </button>
                )}
            </div>

            {/* Video metadata below the thumbnail */}
            <div className="mt-5 text-center">
                <h4 className="font-v2-display text-base md:text-lg text-white leading-snug line-clamp-2 mb-2">
                    {video.title}
                </h4>
                {video.description && (
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed font-medium line-clamp-2 mb-2">
                        {video.description}
                    </p>
                )}
                <span className="font-v2-label text-[10px] text-white/40">
                    {formatDate(video.publishedAt)}
                </span>
            </div>
        </div>
    );
};

const HeroVideoSkeleton = () => (
    <div className="flex flex-col animate-pulse">
        <div className="mx-auto w-full max-w-[220px] aspect-[9/16] rounded-[20px] bg-white/5" />
        <div className="mt-5 flex flex-col items-center gap-2">
            <div className="h-3.5 w-4/5 bg-white/5 rounded" />
            <div className="h-3.5 w-3/5 bg-white/5 rounded" />
            <div className="h-2.5 w-3/4 bg-white/5 rounded mt-1" />
            <div className="h-2.5 w-2/3 bg-white/5 rounded" />
            <div className="h-2 w-1/3 bg-white/5 rounded mt-1" />
        </div>
    </div>
);

const StepCard = ({
    step,
    video,
    isLoading,
}: {
    step: (typeof STEPS)[number];
    video: YouTubeVideo | undefined;
    isLoading: boolean;
}) => {
    const { t } = useTranslation();
    const hasVideoSlot = !!video || isLoading;

    return (
        <div
            className="glass-v2 hover-lift-v2 rounded-[32px] p-7 md:p-8 group relative overflow-hidden h-full flex flex-col"
            data-cursor="hover"
        >
            {/* Hover radial glow */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(500px circle at 50% 0%, hsl(260 90% 65% / 0.14), transparent 65%)",
                }}
            />
            {/* Top accent line */}
            <div
                aria-hidden
                className="absolute top-0 inset-x-8 h-px opacity-40 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(90deg, transparent, hsl(260 90% 65%), hsl(190 90% 50%), transparent)",
                }}
            />

            <div className="relative flex-1 flex flex-col">
                {/* Compact step indicator at the top */}
                <div className="flex items-center justify-between mb-5">
                    <span className="font-v2-label text-[10px] text-white/50">
                        {t("process.step_label", { num: step.num, label: step.labelEn })}
                    </span>
                    {hasVideoSlot && (
                        <span className="inline-flex items-center gap-1.5 font-v2-label text-[10px] text-primary/80">
                            <Play size={9} fill="currentColor" />
                            {t("process.from_workshop")}
                        </span>
                    )}
                </div>

                {/* Video hero block on top */}
                {hasVideoSlot && (
                    <div className="mb-6">
                        {isLoading ? (
                            <HeroVideoSkeleton />
                        ) : video ? (
                            <HeroVideoBlock video={video} />
                        ) : null}
                    </div>
                )}

                {/* Divider */}
                {hasVideoSlot && (
                    <div
                        aria-hidden
                        className="h-px w-full opacity-30 mb-5"
                        style={{
                            background:
                                "linear-gradient(90deg, transparent, hsl(260 90% 65% / 0.8), hsl(190 90% 50% / 0.8), transparent)",
                        }}
                    />
                )}

                {/* Step content at the bottom — smaller than before to give the video more room */}
                <div className="mt-auto">
                    <h3 className="font-v2-display text-lg md:text-xl text-white mb-2">
                        {t(`process.steps.${step.key}.title`)}
                    </h3>
                    <p className="text-sm md:text-[15px] text-muted-foreground leading-relaxed font-medium">
                        {t(`process.steps.${step.key}.desc`)}
                    </p>
                </div>
            </div>
        </div>
    );
};

const StepRow = ({
    step,
    video,
    index,
    isLoading,
}: {
    step: (typeof STEPS)[number];
    video: YouTubeVideo | undefined;
    index: number;
    isLoading: boolean;
}) => {
    const isEven = index % 2 === 0; // 0, 2 → right; 1, 3 → left

    return (
        <motion.li
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
                duration: 0.8,
                ease: [0.23, 1, 0.32, 1],
                delay: index * 0.05,
            }}
            className="relative md:grid md:grid-cols-2 md:gap-12 lg:gap-16 items-stretch"
        >
            {/* Ring node — distinct double-ring style for this timeline */}
            <div
                aria-hidden
                className="hidden md:flex absolute right-1/2 top-10 translate-x-1/2 w-16 h-16 items-center justify-center z-10"
            >
                {/* Outer halo ring */}
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background:
                            "conic-gradient(from 0deg, hsl(260 90% 65% / 0.7), hsl(190 90% 50% / 0.7), hsl(260 90% 65% / 0.7))",
                        padding: "2px",
                        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                        WebkitMask:
                            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                        maskComposite: "exclude",
                        WebkitMaskComposite: "xor",
                    }}
                />
                {/* Inner core */}
                <div
                    className="relative w-11 h-11 rounded-full flex items-center justify-center"
                    style={{
                        background: "var(--gradient-accent-v2)",
                        boxShadow:
                            "0 0 24px hsl(260 90% 65% / 0.55), inset 0 0 10px hsl(190 90% 50% / 0.2)",
                    }}
                >
                    <span className="font-v2-label text-[10px] text-white">
                        {step.num}
                    </span>
                </div>
            </div>

            {/* Card */}
            <div
                className={`${isEven ? "md:col-start-1" : "md:col-start-2"}`}
            >
                <StepCard step={step} video={video} isLoading={isLoading} />
            </div>
        </motion.li>
    );
};

const ProcessWithVideos = () => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });
    const spineScale = useTransform(scrollYProgress, [0.1, 0.9], [0, 1]);

    const { data, isLoading } = useQuery({
        queryKey: ["youtube-videos", HOME_VIDEO_LIMIT],
        queryFn: async () => {
            const res = await socialApi.getYouTubeVideos(HOME_VIDEO_LIMIT);
            return res.data.data;
        },
        staleTime: 30 * 60 * 1000,
        retry: 1,
    });

    const videos = (data?.videos ?? []).slice(0, HOME_VIDEO_LIMIT);

    return (
        <section
            ref={containerRef}
            className="py-20 md:py-32 relative"
            id="process"
        >
            <div className="container mx-auto px-6 relative">
                <div className="max-w-3xl mx-auto text-center mb-20">
                    <p className="font-v2-label text-xs text-primary/80 mb-4">
                        {t("process.eyebrow")}
                    </p>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("process.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("process.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("process.subtitle")}
                    </p>
                </div>

                <div className="relative max-w-6xl mx-auto">
                    {/* Base spine */}
                    <div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px bg-white/[0.06]"
                    />
                    {/* Progressive fill */}
                    <motion.div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px origin-top"
                        style={{
                            scaleY: spineScale,
                            background:
                                "linear-gradient(180deg, hsl(260 90% 65% / 0.9), hsl(190 90% 50% / 0.9), hsl(260 90% 65% / 0.4))",
                        }}
                    />

                    <ol className="space-y-14 md:space-y-16">
                        {STEPS.map((step, i) => (
                            <StepRow
                                key={step.num}
                                step={step}
                                video={videos[i]}
                                index={i}
                                isLoading={isLoading}
                            />
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
};

export default ProcessWithVideos;
